/**
 * Token - 身份标识管理
 *
 * 职责：
 * - anonymous_id: 设备标识，永久存储，没有就生成
 * - user_unique_id: 优先用业务方提供的，否则用 anonymous_id 兜底
 *
 * 轻量级实现：只负责 anonymous_id 的生成和存储
 * user_unique_id 的兜底逻辑在 Collector.buildBatchReportData 中处理
 */

import { StorageWrapper } from './storage'
import { generateUUID, isBrowser } from './utils'

/**
 * Token 缓存数据结构
 */
interface TokenData {
  /** 匿名标识（设备级，永久） */
  anonymous_id: string
  /** 时间戳（用于调试） */
  timestamp: number
}

/**
 * Token 身份标识管理类
 * 1. 首次访问时生成 anonymous_id 并存储
 * 2. 后续访问直接读取
 * 3. 永久存储在 localStorage，跨 session 不变
 */
export class Token {
  private static STORAGE_KEY = '__et_token__'

  private storage: StorageWrapper
  private cachedData: TokenData | null = null

  constructor() {
    this.storage = new StorageWrapper('local')

    if (isBrowser()) {
      this.ensureToken()
    }
  }

  /**
   * 确保 Token 存在
   */
  private ensureToken(): void {
    const data = this.storage.getItem<TokenData>(Token.STORAGE_KEY)

    if (data && data.anonymous_id) {
      this.cachedData = data
    } else {
      this.cachedData = {
        anonymous_id: generateUUID().replace(/-/g, ''),
        timestamp: Date.now(),
      }
      this.storage.setItem(Token.STORAGE_KEY, this.cachedData)
    }
  }

  /**
   * 获取 anonymous_id（设备标识）
   * - 永久存储在 localStorage
   * - 跨 session 保持不变
   * - 用于标识设备/浏览器
   */
  getAnonymousId(): string {
    if (!isBrowser()) {
      return ''
    }

    if (this.cachedData) {
      return this.cachedData.anonymous_id
    }

    this.ensureToken()
    return this.cachedData!.anonymous_id
  }
}
